import React, { useState, useEffect } from "react";
import DashboardPatient from "../dashboard/DashboardPatient";
import ProviderCard from "../widgets/w-providers/ProviderCard";
import SearchModule from "./SearchModule";
import SunshineSVG from "../../images/sunshine.svg";
import Axios from "axios";
import Loading from "../utils/Loading";
import { resizeByLink } from "./../utils/ResizeLinkCloudinary";
import swal from "sweetalert";
import {
  Backdrop,
  CircularProgress,
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Fade,
  Chip,
  IconButton,
  InputAdornment,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useUserState from "../hooks/useUserState";
import useBDocTranslator from "../hooks/useBDocTranslator";
import ModalCompleteInfo from "../patients/ModalCompleteInfo";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import ViewModuleIcon from "@material-ui/icons/ViewModule";
import ViewListIcon from "@material-ui/icons/ViewList";
import PersonIcon from "@material-ui/icons/Person";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#f8f9fa",
    minHeight: "100vh",
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(4),
  },
  container: {
    maxWidth: 1400,
  },
  welcomeCard: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    overflow: "hidden",
    position: "relative",
  },
  welcomeContent: {
    padding: theme.spacing(4),
    position: "relative",
    zIndex: 2,
  },
  welcomeOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(0, 0, 0, 0.1)",
    zIndex: 1,
  },
  searchCard: {
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(2),
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  searchHeader: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    padding: theme.spacing(2),
    borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px 0 0`,
  },
  searchContent: {
    padding: theme.spacing(3),
  },
  resultsHeader: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  resultsCount: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  viewToggle: {
    display: "flex",
    gap: theme.spacing(1),
  },
  filterChip: {
    margin: theme.spacing(0.5),
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(6),
    color: theme.palette.text.secondary,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: "300px",
  },
  providerGrid: {
    marginTop: theme.spacing(1),
  },
  statsCard: {
    background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
    color: "white",
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sunshineIcon: {
    // Remove animation
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
  quickFilter: {
    marginBottom: theme.spacing(2),
  },
  quickFilterButton: {
    margin: theme.spacing(0.5),
    borderRadius: theme.spacing(3),
  },
}));

function MainPatient() {
  const [providers, setProviders] = useState([]);
  const [providersOptions, setProvidersOptions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [specialties, setSpecialties] = useState([]);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedSpecialty, setSelectedSpecialty] = useState("");
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const [showCompleteInfo, setShowCompleteInfo] = useState(false);

  const userLocalState = useUserState();
  const jwtSession = userLocalState[0].jwt;

  const { t, changeDirectLanguage } = useBDocTranslator();

  //this method manage the backdrop status
  const handleClose = () => {
    setOpen(false);
  };

  //Method for search all providers
  const fetchAllProviders = () => {
    try {
      setOpen(true);
      Axios.get(`${process.env.REACT_APP_API_URL_BASE}/provider/enabled`, {
        headers: { Authorization: "Bearer " + jwtSession }, //the jwt is a variable which holds the token
      })
        .then((response) => {
          setOpen(false);
          setLoadingData(false);
          setProviders(response.data);
          setProvidersOptions(response.data);
        })
        .catch((err) => console.log(err));
    } catch (error) {
      console.log(error);
    }
  };

  //Effect to fetch providers
  useEffect(() => {
    fetchAllProviders();
    changeDirectLanguage(userLocalState[0]?.language || "en");
  }, []);

  //Effect to fetch Specialty data
  useEffect(() => {
    Axios.get(
      `${process.env.REACT_APP_API_URL_BASE}/specialty/findAll?exists=1`,
      {
        headers: { Authorization: "Bearer " + jwtSession }, //the jwt is a variable which holds the token
      }
    )
      .then((response) => {
        setLoadingData(false);
        setSpecialties(response.data);
      })
      .catch((err) => console.log(err));

    const showCompleteInfoLocal =
      window.sessionStorage.getItem("showCompleteInfo");

    if (!!userLocalState[0]?.user.completeInfo && !showCompleteInfoLocal) {
      setShowCompleteInfo(true);
    }
  }, []);

  const handleSearch = (language, specialty, provider) => {
    const languageParam = language?.code;
    const specialtyParam = specialty?.speciality_name;
    const providerParam = provider?.firstName; // consider the lastname as well

    if (!languageParam && !specialtyParam && !providerParam) {
      fetchAllProviders();
    } else {
      setOpen(true);

      Axios.get(`${process.env.REACT_APP_API_URL_BASE}/customSearch/provider`, {
        params: {
          language: languageParam,
          specialty: specialtyParam,
          providerName: providerParam,
        },
        headers: { Authorization: "Bearer " + jwtSession }, //the jwt is a variable which holds the token
      })
        .then((response) => {
          setOpen(false);
          setProviders(response.data);
        })
        .catch((err) => {
          console.error(err);
          setOpen(false);
          swal({
            title: t("Failed"),
            text: t("Error fetching Data for Selected Country"),
            icon: "error",
            button: false,
          });
        });
    }
  };

  const handleQuickFilter = (specialtyName) => {
    const specialty = specialties.find(
      (s) => s.speciality_name === specialtyName
    );
    if (specialty) {
      setSelectedSpecialty(specialtyName);
      handleSearch(null, specialty, null);
    }
  };

  const clearFilters = () => {
    setSelectedSpecialty("");
    setSearchTerm("");
    fetchAllProviders();
  };

  const filteredProviders = providers.filter((provider) => {
    const matchesSearch =
      searchTerm === "" ||
      `${provider.firstName} ${provider.lastname}`
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      provider.medical_speciality?.speciality_name
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());

    const matchesSpecialty =
      selectedSpecialty === "" ||
      provider.medical_speciality?.speciality_name === selectedSpecialty;

    return (
      matchesSearch &&
      matchesSpecialty &&
      provider.status_provider.status_name === "APPROVED"
    );
  });

  const approvedProviders = providers.filter(
    (provider) => provider.status_provider.status_name === "APPROVED"
  );

  const topSpecialties = [
    "General Practitioner",
    "Psychology",
    "Nutrition",
    "Cardiology",
  ];

  return (
    <DashboardPatient>
      <div className={classes.root}>
        <Container maxWidth="xl" className={classes.container}>
          <Backdrop
            className={classes.backdrop}
            open={open}
            onClick={handleClose}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          {/* Welcome Banner */}
          <Fade in timeout={800}>
            <Card className={classes.welcomeCard}>
              <div className={classes.welcomeOverlay} />
              <CardContent className={classes.welcomeContent}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      variant="h3"
                      component="h1"
                      gutterBottom
                      style={{ fontWeight: 600 }}
                    >
                      {t("welcome.to.your.dashboard")}!
                    </Typography>
                    <Typography
                      variant="h6"
                      style={{ opacity: 0.9, maxWidth: 600 }}
                    >
                      {t("welcome.dashboard.message")}
                    </Typography>
                  </Box>

                  <Box>
                    <img
                      src={SunshineSVG}
                      alt="sunshine"
                      className={classes.sunshineIcon}
                      width={80}
                      height={80}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>

          {/* Search Section */}
          <Fade in timeout={1200}>
            <Card className={classes.searchCard}>
              <Box className={classes.searchHeader}>
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  <SearchIcon
                    style={{ marginRight: 8, verticalAlign: "middle" }}
                  />
                  {t("Find Your Healthcare Provider")}
                </Typography>
              </Box>
              <CardContent className={classes.searchContent}>
                <SearchModule
                  handleSearch={handleSearch}
                  specialties={specialties}
                  providers={providersOptions}
                  language={userLocalState[0]?.language || "en"}
                />

                {/* Quick Filters */}
                <Box className={classes.quickFilter}>
                  <Typography
                    variant="subtitle1"
                    gutterBottom
                    style={{ fontWeight: 600 }}
                  >
                    {t("Popular Specialties")}
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {topSpecialties.map((specialty) => (
                      <Button
                        key={specialty}
                        variant={
                          selectedSpecialty === specialty
                            ? "contained"
                            : "outlined"
                        }
                        color="primary"
                        size="small"
                        className={classes.quickFilterButton}
                        onClick={() => handleQuickFilter(specialty)}
                      >
                        {t(specialty)}
                      </Button>
                    ))}
                    {(selectedSpecialty || searchTerm) && (
                      <Button
                        variant="outlined"
                        size="small"
                        className={classes.quickFilterButton}
                        onClick={clearFilters}
                      >
                        {t("Clear Filters")}
                      </Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Fade>

          {/* Results Header */}
          <Box className={classes.resultsHeader}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box className={classes.resultsCount}>
                <PersonIcon color="primary" />
                <Typography variant="h6" style={{ fontWeight: 600 }}>
                  {filteredProviders.length} {t("Healthcare Providers Found")}
                </Typography>
                {(selectedSpecialty || searchTerm) && (
                  <Chip
                    label={selectedSpecialty || searchTerm}
                    size="small"
                    color="primary"
                    onDelete={clearFilters}
                    className={classes.filterChip}
                  />
                )}
              </Box>

              <Box className={classes.viewToggle}>
                <IconButton
                  color={viewMode === "grid" ? "primary" : "default"}
                  onClick={() => setViewMode("grid")}
                >
                  <ViewModuleIcon />
                </IconButton>
                <IconButton
                  color={viewMode === "list" ? "primary" : "default"}
                  onClick={() => setViewMode("list")}
                >
                  <ViewListIcon />
                </IconButton>
              </Box>
            </Box>
          </Box>

          {/* Loading State */}
          {loadingData && (
            <Box className={classes.loadingContainer}>
              <Loading color="#3788d8d4" />
            </Box>
          )}

          {/* No Results */}
          {!loadingData && filteredProviders.length === 0 && (
            <Card>
              <CardContent>
                <Box className={classes.noResults}>
                  <PersonIcon
                    style={{ fontSize: 64, marginBottom: 16, opacity: 0.3 }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t("No providers found")}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {t(
                      "Try adjusting your search criteria or browse all available providers"
                    )}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{ marginTop: 16 }}
                    onClick={clearFilters}
                  >
                    {t("Show All Providers")}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Providers Grid */}
          {!loadingData && filteredProviders.length > 0 && (
            <Fade in timeout={1600}>
              <Grid container spacing={3} className={classes.providerGrid}>
                {filteredProviders.map((item, index) => {
                  const fullname = `${item.firstName} ${item.lastname}`;
                  let profileImageURL;

                  if (item.user?.user_images?.length > 0) {
                    profileImageURL = resizeByLink(
                      item.user.user_images.at(-1).path_resource,
                      "upload"
                    );
                  }

                  const specialitiesParsed = Array.of(
                    item.medical_speciality?.speciality_name
                  );

                  return (
                    <Grid
                      item
                      xs={12}
                      md={viewMode === "grid" ? 6 : 12}
                      lg={viewMode === "grid" ? 4 : 12}
                      key={item.id}
                    >
                      <Fade in timeout={800 + index * 100}>
                        <div>
                          <ProviderCard
                            id={item.id}
                            title={item.title}
                            fullname={fullname}
                            profileImage={profileImageURL}
                            specialty={specialitiesParsed}
                            providerId={item.id}
                            country={item.country}
                            providerImg={item.profile_image}
                            summary={item.summary}
                            providerUserId={item?.user?.id}
                            rating={item.rating_avg}
                            available={item?.availability}
                            plans={item?.provider_plans}
                            locations={item?.provider_locations}
                          />
                        </div>
                      </Fade>
                    </Grid>
                  );
                })}
              </Grid>
            </Fade>
          )}

          <ModalCompleteInfo
            open={showCompleteInfo}
            close={() => setShowCompleteInfo(false)}
          />
        </Container>
      </div>
    </DashboardPatient>
  );
}

export default MainPatient;
